/**
 * Environment Utilities
 * Helpers for mapping frontend environment strings to backend environment values
 */

import config from '../config/environment';

/**
 * Get backend environment from REACT_APP_ENVIRONMENT env variable.
 * Maps frontend environment strings to backend-compatible values.
 *
 * Rules:
 * - In DEV_MODE, unset/dev/development returns null so the local shared catalog
 *   can expose its TEST/PRD rows; an explicit test/qa/staging value still wins.
 * - dev/development -> "dev"
 * - prod/production/prd -> "prd"
 * - Other values: returns lowercase version
 *
 * @returns {string|null} Backend environment string ("dev" or "prd") or null
 */
export function getBackendEnvironment() {
  let frontendEnvironment = config.AGENT_ENVIRONMENT || null;

  // A local .env commonly says "dev" to describe the process, while its shared
  // catalog rows are stamped TEST/PRD. Do not turn that into an empty DEV-only
  // query. Developers can still opt into TEST explicitly with
  // REACT_APP_ENVIRONMENT=test.
  if (config.DEV_MODE) {
    const localEnvironment = String(frontendEnvironment || '').trim().toLowerCase();
    if (!localEnvironment || localEnvironment === 'dev' || localEnvironment === 'development') {
      return null;
    }
  }

  // Fallback when stale/missing env-config.js (e.g. long-cached empty window._env_)
  if (!frontendEnvironment && typeof window !== 'undefined' && window.location?.hostname) {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return null;
    }
    if (host.includes('.run.app')) {
      return 'prd';
    }
  }

  if (!frontendEnvironment) {
    return null;
  }

  // Map to backend environment
  const envLower = frontendEnvironment.toLowerCase();

  if (envLower === "dev" || envLower === "development") {
    return "dev";
  }

  if (
    envLower === "prod" ||
    envLower === "production" ||
    envLower === "prd"
  ) {
    return "prd";
  }

  // Return lowercase for any other values
  return envLower;
}

/**
 * Canonical deploy environment for domain_configs.enabled_environments filtering.
 * Values: "test" | "prod" | "dev" | null (no filter).
 *
 * @returns {string|null}
 */
export function getDomainDeployEnvironment() {
  if (config.DEV_MODE) {
    return "dev";
  }

  const frontendEnvironment = config.AGENT_ENVIRONMENT || null;
  if (!frontendEnvironment) {
    return null;
  }

  const envLower = frontendEnvironment.toLowerCase();

  if (envLower === "prod" || envLower === "production" || envLower === "prd") {
    return "prod";
  }

  if (envLower === "test" || envLower === "qa" || envLower === "staging") {
    return "test";
  }

  if (envLower === "dev" || envLower === "development") {
    return "dev";
  }

  return envLower;
}

/**
 * @param {string[]|null|undefined} enabledEnvironments
 * @param {string|null} deployEnvironment from getDomainDeployEnvironment()
 * @returns {boolean}
 */
export function domainEnabledForDeployEnvironment(enabledEnvironments, deployEnvironment) {
  if (!Array.isArray(enabledEnvironments) || enabledEnvironments.length === 0) {
    return true;
  }

  const normalize = (value) => {
    const raw = String(value || "").trim().toLowerCase();
    if (!raw) return null;
    if (raw === "prod" || raw === "production" || raw === "prd") return "prod";
    if (raw === "test" || raw === "qa" || raw === "staging") return "test";
    if (raw === "dev" || raw === "development") return "dev";
    return raw;
  };

  const enabled = enabledEnvironments.map(normalize).filter(Boolean);
  if (enabled.length === 0) return true;

  const current = normalize(deployEnvironment);
  if (!current || current === "dev") return true;

  return enabled.includes(current);
}
